/**
 * Preflight analysis page.
 * 
 * Phase 04: Display static preflight analysis results.
 */

import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import type { PreflightResponse } from '../lib/api'
import { useLocale } from '../contexts/LocaleContext'
import { severityLabel } from '../lib/i18n'

function PreflightPage() {
  const { t, locale } = useLocale()
  const { preflightId } = useParams<{ preflightId: string }>()
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!preflightId) return

    const fetchPreflight = async () => {
      try {
        setIsLoading(true)
        const data = await api.getPreflight(preflightId)
        setPreflight(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('preflight.failed'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchPreflight()
  }, [preflightId])

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-[var(--text-secondary)]">{t('preflight.loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card card-error">
        <h3>{t('common.error')}</h3>
        <p>{error}</p>
      </div>
    )
  }

  if (!preflight) {
    return (
      <div className="card">
        <h3>{t('preflight.notFound')}</h3>
        <p className="text-[var(--text-secondary)]">{t('preflight.notFoundBody')}</p>
      </div>
    )
  }

  const statusBadge = () => {
    switch (preflight.status) {
      case 'succeeded':
        return <span className="badge badge-success">{t('preflight.succeeded')}</span>
      case 'succeeded_with_warnings':
        return <span className="badge badge-warning">{t('preflight.succeededWarn')}</span>
      case 'blocked':
        return <span className="badge badge-error">{t('preflight.blockedStatus')}</span>
      case 'failed':
        return <span className="badge badge-error">{t('preflight.failedStatus')}</span>
      default:
        return <span className="badge badge-info">{preflight.status.toUpperCase()}</span>
    }
  }

  const riskBadge = (level: string) => {
    const label = locale === 'en' ? level.toUpperCase() : severityLabel(locale, level)
    const cls =
      level === 'critical' || level === 'high'
        ? 'badge-error'
        : level === 'medium'
          ? 'badge-warning'
          : 'badge-info'
    return <span className={`badge ${cls} text-xs`}>{label}</span>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <h2>{t('preflight.title')}</h2>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-xs text-[var(--text-secondary)] mb-1">{t('fixRuns.colStatus')}</p>
            {statusBadge()}
          </div>
          <div>
            <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.scannerVersion')}</p>
            <p className="font-mono text-sm">{preflight.scanner_version}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.projectId')}</p>
            <p className="font-mono text-xs">{preflight.project_id}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.sourceId')}</p>
            <p className="font-mono text-xs">{preflight.source_id}</p>
          </div>
        </div>
      </div>

      {/* Decision */}
      {preflight.decision && (
        <div className={`card ${!preflight.decision.safe_to_proceed ? 'card-error' : ''}`}>
          <h3>{t('preflight.decision')}</h3>
          <p className="mb-2">
            <strong>{preflight.decision.safe_to_proceed ? `✓ ${t('preflight.safe')}` : `✗ ${t('preflight.blocked')}`}</strong>
          </p>
          
          {preflight.decision.reasons.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.reasons')}</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {preflight.decision.reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </div>
          )}

          {preflight.decision.blockers.length > 0 && (
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.blockers')}</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {preflight.decision.blockers.map((blocker, i) => (
                  <li key={i} className="text-[var(--accent-gold)]">{blocker}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Scan Statistics */}
      {preflight.scan_statistics && (
        <div className="card">
          <h3>{t('preflight.stats')}</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.totalFiles')}</p>
              <p className="text-2xl font-mono">{preflight.scan_statistics.total_files}</p>
            </div>
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.scanned')}</p>
              <p className="text-2xl font-mono">{preflight.scan_statistics.scanned_files}</p>
            </div>
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1">{t('preflight.skipped')}</p>
              <p className="text-2xl font-mono">{preflight.scan_statistics.skipped_files}</p>
            </div>
          </div>
        </div>
      )}

      {/* Detected Technologies */}
      {preflight.detected_technologies.length > 0 && (
        <div className="card">
          <h3>{t('preflight.tech', { n: preflight.detected_technologies.length })}</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('preflight.colName')}</th>
                  <th>{t('preflight.colCategory')}</th>
                  <th>{t('preflight.colVersion')}</th>
                  <th>{t('preflight.colConfidence')}</th>
                </tr>
              </thead>
              <tbody>
                {preflight.detected_technologies.map((tech, i) => (
                  <tr key={i}>
                    <td className="font-medium">{tech.name}</td>
                    <td className="text-[var(--text-secondary)] text-xs">{tech.category}</td>
                    <td className="font-mono text-xs">{tech.version || '—'}</td>
                    <td className="text-xs">{tech.confidence || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Risky Artifacts */}
      {preflight.risky_artifacts.length > 0 && (
        <div className="card">
          <h3>{t('preflight.risky', { n: preflight.risky_artifacts.length })}</h3>
          <div className="space-y-2">
            {preflight.risky_artifacts.slice(0, 20).map((artifact, i) => (
              <div key={i} className="border" style={{ borderColor: 'var(--border-subtle)' }}>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <code className="text-xs">{artifact.file_path}</code>
                    {riskBadge(artifact.risk_level)}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {artifact.type} · {artifact.reason}
                  </p>
                </div>
              </div>
            ))}
            {preflight.risky_artifacts.length > 20 && (
              <p className="text-xs text-[var(--text-secondary)] text-center">
                {t('preflight.andMore', { n: preflight.risky_artifacts.length - 20 })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Secret Indicators */}
      {preflight.secret_indicators.length > 0 && (
        <div className="card card-warning">
          <h3>{t('preflight.secrets', { n: preflight.secret_indicators.length })}</h3>
          <p className="text-xs text-[var(--text-secondary)] mb-4">
            {t('preflight.secretsHint')}
          </p>
          <div className="space-y-2">
            {preflight.secret_indicators.slice(0, 10).map((secret, i) => (
              <div key={i} className="border" style={{ borderColor: 'var(--border-subtle)' }}>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <code className="text-xs">{secret.file_path}</code>
                    {riskBadge(secret.severity)}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {secret.type} · {t('preflight.line', { n: secret.line_number || '?' })}
                  </p>
                  <p className="font-mono text-xs mt-1">{secret.redacted_preview}</p>
                </div>
              </div>
            ))}
            {preflight.secret_indicators.length > 10 && (
              <p className="text-xs text-[var(--text-secondary)] text-center">
                {t('preflight.andMore', { n: preflight.secret_indicators.length - 10 })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Declared Commands */}
      {preflight.declared_commands.length > 0 && (
        <div className="card">
          <h3>{t('preflight.commands', { n: preflight.declared_commands.length })}</h3>
          <p className="text-xs text-[var(--text-secondary)] mb-4">
            {t('preflight.commandsHint')}
          </p>
          <div className="space-y-2">
            {preflight.declared_commands.slice(0, 15).map((cmd, i) => (
              <div key={i} className={`border ${cmd.is_risky ? 'border-[var(--accent-gold)]' : ''}`} style={{ borderColor: cmd.is_risky ? undefined : 'var(--border-subtle)' }}>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <code className="text-xs font-medium">{cmd.name}</code>
                    {cmd.is_risky && <span className="badge badge-warning text-xs">{t('preflight.riskyBadge')}</span>}
                  </div>
                  <code className="text-xs text-[var(--text-secondary)] block mb-1">{cmd.command}</code>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {t('preflight.source', { file: cmd.source_file })}
                    {cmd.risk_reason && ` · ${cmd.risk_reason}`}
                  </p>
                </div>
              </div>
            ))}
            {preflight.declared_commands.length > 15 && (
              <p className="text-xs text-[var(--text-secondary)] text-center">
                {t('preflight.andMore', { n: preflight.declared_commands.length - 15 })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PreflightPage
